import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const SchoolStats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({
    students: 0,
    teachers: 0,
    alumni: 0,
    years: 0
  });
  const sectionRef = useRef(null);

  const stats = useMemo(() => [
    { key: 'students', target: 600, suffix: '+', label: 'Current Students' },
    { key: 'teachers', target: 30, suffix: '+', label: 'Dedicated Teachers' },
    { key: 'alumni', target: 10000, suffix: '+', label: 'Alumni Network' },
    { key: 'years', target: 20, suffix: '+', label: 'Years of Excellence' }
  ], []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            stats.forEach((stat) => animateCount(stat.key, stat.target));
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [stats]);

  const animateCount = (key, target) => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCounts(prev => ({ ...prev, [key]: target }));
        clearInterval(timer);
      } else {
        setCounts(prev => ({ ...prev, [key]: Math.floor(current) }));
      }
    }, duration / steps);
  };

  return (
    <section ref={sectionRef} className="mb-16">
      <motion.div 
        className="bg-white/80 dark:bg-gray-900/70 backdrop-blur-md py-12 rounded-xl shadow-xl transition-all duration-1000"
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.key}
                className="rounded-lg p-6 bg-white dark:bg-slate-800 shadow-md hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.2, duration: 0.6 }}
              >
                <h3 className="text-5xl md:text-6xl font-extrabold text-primary dark:text-white mb-2">
                  {counts[stat.key].toLocaleString()}{stat.suffix}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default SchoolStats;
